import { SeriesPoint } from 'd3-shape';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FilteredGeneratorOutput } from '../../../hooks/useGeneratorOutput';
import { ColorScale } from '../../../utils/ColorScale';
import { DataKeyType, LabelGetterProp, ValueGetterProp } from '../../../utils/DataProps';
import { mapToSortedArray } from '../../../utils/mapToSortedArray';
import { NumberCoercible } from '../../../utils/NumberCoercible';
import { NumberScale } from '../../../utils/NumberScale';
import { PointInfo } from '../../../utils/PointInfo';
import { TimeScale } from '../../../utils/TimeScale';
import { XGetterProp, YGetterProp } from '../../../utils/XYDataProps';
import { TICK_LINE_SIZE_PX } from '../../Axis';
import { Guideline } from '../Guideline';
import { InteractiveOverlay, InteractivePointEvent } from '../InteractiveOverlay';
import { PointClickEventHandler } from '../XYChartProps';

const interactiveOverlayYGetter = (d: { generatedValue: number }): number => d.generatedValue;
/**
 * Extended data related to a point that was output by a generator (e.g., a stack generator).
 */
export interface GeneratedPointInfo<TDataKeyType, TPointType, TDatum> {
  /**
   * The associated point produced by the generator.
   */
  generatedPoint: SeriesPoint<
    [x: NumberCoercible, yValuesMap: Map<TDataKeyType, PointInfo<TPointType, TDatum, NumberCoercible | undefined>>]
  >;
  /**
   * An unscaled value corresponding to the point's generated position (e.g., including any
   * stacking).
   */
  generatedValue: number;
  /**
   * The original series associated with the point.
   */
  series: Readonly<TDatum>;
  /**
   * The data key of the original series.
   */
  seriesDataKey: TDataKeyType;
  /**
   * The index of the original series.
   */
  seriesIndex: number;
}

interface UseInteractiveOverlayParams<TDataKeyType extends DataKeyType, TPointType, TDatum>
  extends LabelGetterProp<TDatum>,
    ValueGetterProp<TDatum, TPointType[]>,
    XGetterProp<TPointType>,
    YGetterProp<TPointType> {
  /**
   * The color scale to use.
   */
  colorScale: ColorScale<TDataKeyType>;
  /**
   * The chart data.
   */
  data: readonly Readonly<TDatum>[];
  /**
   * The filtered generator output.
   */
  filteredGeneratorOutput: FilteredGeneratorOutput<TDataKeyType, TPointType, TDatum>[];
  /**
   * A callback to invoke when a point is clicked.
   */
  onPointClick: PointClickEventHandler<TPointType, TDataKeyType, TDatum> | undefined;
  /**
   * The x-scale used for display.
   */
  xScale: TimeScale;
  /**
   * The y-scale used for display.
   */
  yScale: NumberScale;
}

export interface UseInteractiveOverlayReturnType<TDataKeyType extends DataKeyType, TPointType, TDatum> {
  /**
   * Data about the active interactive point, if any.
   */
  activePointInfo: GeneratedPointInfo<TDataKeyType, TPointType, TDatum> | undefined;
  /**
   * A React node to use to render a guideline along the active interactive point, if any.
   */
  guideline: React.ReactNode;
  /**
   * A React node to use to render the interactive overlay itself, which is necessary to capture
   * interactions and determine the active point.
   */
  interactiveOverlay: React.ReactNode;
}

export const useInteractiveOverlay = <TDataKeyType extends DataKeyType, TPointType, TDatum>({
  colorScale,
  data,
  filteredGeneratorOutput,
  onPointClick,
  labelGetter,
  valueGetter,
  xGetter,
  xScale,
  yGetter,
  yScale,
}: UseInteractiveOverlayParams<TDataKeyType, TPointType, TDatum>): UseInteractiveOverlayReturnType<
  TDataKeyType,
  TPointType,
  TDatum
> => {
  type GeneratedPointInfoLocal = GeneratedPointInfo<TDataKeyType, TPointType, TDatum>;

  const [activePointInfo, setActivePointInfo] = useState<GeneratedPointInfoLocal | undefined>();

  const handleHoverPointChange = useCallback(
    ({ activePoint }: InteractivePointEvent<GeneratedPointInfoLocal | undefined>) => {
      setActivePointInfo(activePoint);
    },
    [],
  );

  const handleInteractiveOverlayPointClick = useMemo(() => {
    if (!onPointClick) {
      return undefined;
    }

    return ({ activePoint, domEvent }: InteractivePointEvent<GeneratedPointInfoLocal>) => {
      const originalSeries = activePoint.series;
      const dataKey = activePoint.seriesDataKey;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- key should be guaranteed to exist
      const pointInfo = activePoint.generatedPoint.data[1].get(dataKey)!;

      const { point: originalPoint } = pointInfo;
      const label = labelGetter(originalSeries);
      const color = colorScale.get(dataKey);

      onPointClick({
        color,
        data,
        dataKey,
        domEvent,
        index: activePoint.seriesIndex,
        item: originalSeries,
        label,
        value: valueGetter(originalSeries),
        point: originalPoint,
        x: xGetter(originalPoint),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value must be non-null or the point shouldn't have been rendered
        y: yGetter(originalPoint)!,
      });
    };
  }, [colorScale, data, labelGetter, onPointClick, valueGetter, xGetter, yGetter]);

  const interactiveOverlayPoints = useMemo(() => {
    const map = filteredGeneratorOutput.reduce((acc, { generatedPoints, seriesDataKey }) => {
      generatedPoints.forEach((generatedPoint, seriesIndex) => {
        const x = generatedPoint.data[0];
        const y = generatedPoint[1];

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- key should be guaranteed to exist
        const { series } = generatedPoint.data[1].get(seriesDataKey)!;

        const row = acc.get(x) ?? [];
        row.push({
          generatedValue: y,
          generatedPoint,
          series,
          seriesDataKey,
          seriesIndex,
        });
        acc.set(x, row);
      });

      return acc;
    }, new Map<NumberCoercible, GeneratedPointInfoLocal[]>());

    return mapToSortedArray(map);
  }, [filteredGeneratorOutput]);

  const interactiveOverlay = (
    <InteractiveOverlay
      xScale={xScale}
      yScale={yScale}
      yGetter={interactiveOverlayYGetter}
      data={interactiveOverlayPoints}
      onHoverPointChange={handleHoverPointChange}
      onClick={handleInteractiveOverlayPointClick}
    />
  );

  const [yMax, yMin] = yScale.range();

  const guidelineXValue = activePointInfo?.generatedPoint.data[0];
  const lastXValue = useRef(guidelineXValue?.valueOf() ?? 0);
  useEffect(() => {
    if (guidelineXValue !== undefined) {
      lastXValue.current = guidelineXValue.valueOf();
    }
  }, [guidelineXValue]);

  const guideline = useMemo(() => {
    const x = guidelineXValue !== undefined ? xScale(guidelineXValue.valueOf()) : undefined;
    const effectiveX = x ?? xScale(lastXValue.current) ?? 0;

    return <Guideline opacity={x === undefined ? 0 : 1} x={effectiveX} y1={yMax + TICK_LINE_SIZE_PX} y2={yMin} />;
  }, [guidelineXValue, xScale, yMax, yMin]);

  return {
    activePointInfo,
    guideline,
    interactiveOverlay,
  };
};
